// client/src/modals/RequestExternalUploadModal.jsx
import React, { useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaEnvelope,
  FaShareAlt,
  FaRegCopy,
} from "react-icons/fa";
import { sessionRequestConfig } from "../services/sessionLocal";

const BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

function formatDurationLeft(expiresAtIso) {
  if (!expiresAtIso) return "";
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return "0m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RequestExternalUploadModal = ({
  isOpen,
  onClose,
  tenantId,
  eventId,
  itemId,
  eventName = "",
  itemName = "",
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("compose"); // compose | creating | ready | error
  const [linkUrl, setLinkUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");

  const emailValid = useMemo(() => EMAIL_REGEX.test(email.trim()), [email]);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  if (!isOpen) return null;

  const createEndpoint = itemId
    ? `${BASE_URL}/api/external-upload/tenants/${tenantId}/events/${eventId}/items/${itemId}/links`
    : `${BASE_URL}/api/external-upload/tenants/${tenantId}/events/${eventId}/links`;

  const requestLink = async (shouldSendEmail) => {
    setPhase("creating");
    setError("");
    try {
      const body = {
        recipientEmail: email.trim() || null,
        sendEmail: !!shouldSendEmail,
      };
      const { data } = await axios.post(
        createEndpoint,
        body,
        sessionRequestConfig()
      );
      if (!data?.success) {
        throw new Error(data?.error || "create_failed");
      }
      setLinkUrl(data.url);
      setExpiresAt(data.expiresAt);
      setEmailSent(!!data.emailSent);
      setPhase("ready");
      if (shouldSendEmail && data.emailSent) {
        toast.success(t("events.externalUpload.emailSent"));
      } else if (shouldSendEmail && !data.emailSent) {
        toast.warn(t("events.externalUpload.error.generic"));
      }
    } catch (err) {
      console.error("[RequestExternalUploadModal] create failed:", err);
      setError(err?.response?.data?.error || err?.message || "error");
      setPhase("error");
    }
  };

  const handleSendEmail = () => {
    if (!emailValid) return;
    if (phase === "ready" && linkUrl) {
      // Re-send via fresh POST with same email.
      requestLink(true);
    } else {
      requestLink(true);
    }
  };

  const ensureLinkThen = async (cb) => {
    if (phase === "ready" && linkUrl) {
      cb(linkUrl);
      return;
    }
    setPhase("creating");
    try {
      const body = {
        recipientEmail: email.trim() || null,
        sendEmail: false,
      };
      const { data } = await axios.post(
        createEndpoint,
        body,
        sessionRequestConfig()
      );
      if (!data?.success) throw new Error(data?.error || "create_failed");
      setLinkUrl(data.url);
      setExpiresAt(data.expiresAt);
      setEmailSent(false);
      setPhase("ready");
      cb(data.url);
    } catch (err) {
      console.error("[RequestExternalUploadModal] create failed:", err);
      setError(err?.response?.data?.error || err?.message || "error");
      setPhase("error");
    }
  };

  const handleShare = () => {
    ensureLinkThen(async (url) => {
      try {
        await navigator.share({
          title: t("events.externalUpload.title"),
          text: eventName || t("events.externalUpload.title"),
          url,
        });
      } catch (_) {
        // user cancelled or share unsupported — no-op
      }
    });
  };

  const handleCopy = () => {
    ensureLinkThen(async (url) => {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t("events.externalUpload.copied"));
      } catch (_) {
        // Fallback: prompt
        window.prompt(t("events.externalUpload.copyLink"), url);
      }
    });
  };

  const handleClose = () => {
    // Reset state on close so next open is fresh.
    setEmail("");
    setPhase("compose");
    setLinkUrl("");
    setExpiresAt("");
    setEmailSent(false);
    setError("");
    onClose();
  };

  const subject = itemName
    ? `${eventName} — ${itemName}`
    : eventName;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        className="modal-content modal-light"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          maxWidth: "520px",
          width: "100%",
          padding: "24px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                backgroundColor: "#e6f2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1d6fd8",
              }}
            >
              <FaCloudUploadAlt size={22} />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                {t("events.externalUpload.title")}
              </h2>
              {subject && (
                <div style={{ fontSize: "13px", color: "#666" }}>{subject}</div>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              color: "#666",
              fontSize: "18px",
            }}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Description */}
        <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#444", lineHeight: 1.5 }}>
          {t("events.externalUpload.description")}
        </p>

        {/* Email input */}
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="extUploadEmail"
            style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#333", marginBottom: "6px" }}
          >
            {t("events.externalUpload.recipientEmailLabel")}
          </label>
          <input
            id="extUploadEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("events.externalUpload.emailPlaceholder")}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: "14px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Ready-state: URL + expiry */}
        {phase === "ready" && linkUrl && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: "#f5f7fa",
              border: "1px solid #e3e7ed",
              borderRadius: "8px",
            }}
          >
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
              {t("events.externalUpload.expiresIn", { duration: formatDurationLeft(expiresAt) })}
            </div>
            <input
              readOnly
              value={linkUrl}
              onFocus={(e) => e.currentTarget.select()}
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: "13px",
                border: "1px solid #d4d9e0",
                borderRadius: "6px",
                background: "#fff",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}

        {phase === "error" && (
          <div
            style={{
              marginBottom: "16px",
              padding: "10px 12px",
              background: "#fee",
              border: "1px solid #fbb",
              borderRadius: "8px",
              color: "#c00",
              fontSize: "13px",
            }}
          >
            {error || t("events.externalUpload.error.generic")}
          </div>
        )}

        {/* Actions */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handleCopy}
            disabled={phase === "creating"}
            className="btn btn-outline"
            style={{
              padding: "10px 14px",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
              color: "#333",
              cursor: phase === "creating" ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FaRegCopy size={14} />
            {t("events.externalUpload.copyLink")}
          </button>

          {canShare && (
            <button
              onClick={handleShare}
              disabled={phase === "creating"}
              className="btn btn-outline"
              style={{
                padding: "10px 14px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "6px",
                border: "1px solid #ddd",
                backgroundColor: "#fff",
                color: "#333",
                cursor: phase === "creating" ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaShareAlt size={14} />
              {t("events.externalUpload.share")}
            </button>
          )}

          <button
            onClick={handleSendEmail}
            disabled={!emailValid || phase === "creating" || (phase === "ready" && emailSent)}
            className="btn btn-primary"
            style={{
              padding: "10px 14px",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "6px",
              border: "none",
              backgroundColor: !emailValid || (phase === "ready" && emailSent) ? "#9cb8d9" : "#1d6fd8",
              color: "#fff",
              cursor: !emailValid || phase === "creating" || (phase === "ready" && emailSent) ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FaEnvelope size={14} />
            {phase === "creating"
              ? t("events.externalUpload.creating")
              : phase === "ready" && emailSent
                ? t("events.externalUpload.emailSent")
                : t("events.externalUpload.sendEmail")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestExternalUploadModal;
